import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { vendorRfqsAPI } from '../../services/api';
import { printStyles, CompanyLetterhead } from './shared';

const BASIS_LABEL = { per_piece: 'per pc', per_100: 'per 100 pcs', per_1000: 'per 1000 pcs' };
const PIECES = { per_piece: 1, per_100: 100, per_1000: 1000 };

const BLANK_TERMS = [
  'Packing terms', 'Delivery terms / Incoterms', 'Material grade / standard',
  'Payment terms', 'Quotation validity', 'Taxes / duties', 'Country of origin',
];

const formatDate = (value) => value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const number = (value, digits = 0) => (value === null || value === undefined || value === '')
  ? '' : Number(value).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });

export default function VendorRfqPrintView() {
  const { id } = useParams();
  const [rfq, setRfq] = useState(null);
  const [printTriggered, setPrintTriggered] = useState(false);
  const isPreviewMode = new URLSearchParams(window.location.search).get('preview') === 'true';

  useEffect(() => { vendorRfqsAPI.get(id).then((response) => setRfq(response.data)); }, [id]);

  useEffect(() => {
    if (rfq && !printTriggered && !isPreviewMode) {
      setPrintTriggered(true);
      const timer = setTimeout(() => window.print(), 600);
      return () => clearTimeout(timer);
    }
  }, [rfq, printTriggered, isPreviewMode]);

  if (!rfq) {
    return (
      <div className="loading-container" data-testid="vendor-rfq-print-loading">
        <style>{printStyles}</style>
        <div className="spinner" />
      </div>
    );
  }

  const currency = (rfq.currency || 'INR').toUpperCase();
  const basis = PIECES[rfq.price_basis] ? rfq.price_basis : 'per_piece';
  const items = rfq.items || [];
  const totalQty = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const totalWeightKg = items.reduce(
    (sum, item) => sum + (Number(item.quantity || 0) * Number(item.net_weight_grams || 0)) / 1000, 0);

  return (
    <div className="print-container" data-testid="vendor-rfq-print-view">
      <style>{printStyles}</style>
      {!isPreviewMode && (
        <button className="download-pdf-btn no-print" onClick={() => window.print()} data-testid="vendor-rfq-print-button">
          Print / Save PDF
        </button>
      )}

      <CompanyLetterhead documentType="Request for Quotation" documentNumber={rfq.rfq_number} />

      <div className="info-grid">
        <div className="info-box">
          <h4>To (Vendor)</h4>
          <p className="value" data-testid="vendor-rfq-print-vendor">{rfq.vendor_name}</p>
          <p>{rfq.vendor_email || '—'}</p>
          {rfq.vendor_country && <p>{rfq.vendor_country}</p>}
          {rfq.vendor_code && <p>Vendor code: {rfq.vendor_code}</p>}
        </div>
        <div className="info-box">
          <h4>RFQ details</h4>
          <p>RFQ no: <span className="value" data-testid="vendor-rfq-print-number">{rfq.rfq_number}</span></p>
          <p>Issued: <span className="value">{formatDate(rfq.created_at)}</span></p>
          <p>Response due: <span className="value">{rfq.due_date ? formatDate(rfq.due_date) : 'At earliest'}</span></p>
          <p>Quote in: <span className="value" data-testid="vendor-rfq-print-currency">{currency} {BASIS_LABEL[basis]}</span></p>
        </div>
      </div>

      <table className="items-table" data-testid="vendor-rfq-print-items">
        <thead>
          <tr>
            <th style={{ width: '5%' }}>#</th>
            <th style={{ width: '14%' }}>Part No</th>
            <th style={{ width: '27%' }}>Description</th>
            <th style={{ width: '9%', textAlign: 'right' }}>Qty</th>
            <th style={{ width: '6%' }}>UOM</th>
            <th style={{ width: '9%', textAlign: 'right' }}>Wt / pc (g)</th>
            <th style={{ width: '9%', textAlign: 'right' }}>Total Wt (Kgs)</th>
            <th style={{ width: '11%', textAlign: 'right' }}>Price ({currency} / {BASIS_LABEL[basis].replace('per ', '')})</th>
            <th style={{ width: '10%', textAlign: 'right' }}>Total Amount ({currency})</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={item.id} data-testid={`vendor-rfq-print-row-${index + 1}`}>
              <td>{index + 1}</td>
              <td style={{ fontFamily: 'Consolas, monospace' }}>{item.part_number || '—'}</td>
              <td>{item.description}</td>
              <td className="number">{number(item.quantity)}</td>
              <td style={{ textAlign: 'center' }}>{item.quantity_unit || 'pcs'}</td>
              <td className="number">{item.net_weight_grams ? number(item.net_weight_grams, 3) : ''}</td>
              <td className="number">
                {item.net_weight_grams ? number((Number(item.quantity || 0) * Number(item.net_weight_grams)) / 1000, 3) : ''}
              </td>
              <td className="number" />
              <td className="number" />
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f1f5f9', fontWeight: 'bold' }} data-testid="vendor-rfq-print-total-row">
            <td colSpan={3} style={{ textAlign: 'right' }}>TOTAL</td>
            <td className="number">{number(totalQty)}</td>
            <td />
            <td />
            <td className="number">{totalWeightKg ? number(totalWeightKg, 3) : ''}</td>
            <td />
            <td />
          </tr>
        </tfoot>
      </table>

      <div className="terms-section" data-testid="vendor-rfq-print-terms">
        <h4>Commercial terms — to be filled by the vendor</h4>
        {BLANK_TERMS.map((label) => (
          <p key={label} data-testid={`vendor-rfq-term-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            {label}: <span style={{ display: 'inline-block', minWidth: '320px', borderBottom: '1px dotted #92400e' }} />
          </p>
        ))}
        <p>Weight per piece: please confirm or fill where it is left blank above.</p>
      </div>

      <div className="info-box" style={{ marginBottom: '20px' }}>
        <h4>Please include with your offer</h4>
        <p>Unit pricing in {currency} {BASIS_LABEL[basis]}, MOQ, tooling charges, applicable taxes, delivery lead time,
          payment terms, quotation validity and material / quality compliance.</p>
        {rfq.remarks && <p><strong>Our remarks:</strong> {rfq.remarks}</p>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px', fontSize: '12px' }}>
        <div>
          <p style={{ fontWeight: 600 }}>For Singhania International Limited</p>
          <p style={{ marginTop: '36px', borderTop: '1px solid #94a3b8', paddingTop: '4px' }}>Authorised signatory</p>
        </div>
        <div>
          <p style={{ fontWeight: 600 }}>Vendor acceptance</p>
          <p style={{ marginTop: '36px', borderTop: '1px solid #94a3b8', paddingTop: '4px' }}>Signature &amp; stamp</p>
        </div>
      </div>

      <div className="footer">
        This RFQ is linked to inquiry {rfq.inquiry_number} for complete commercial traceability.
      </div>
    </div>
  );
}
